import { Menu, MenuItem, Stack, Typography } from '@mui/material'
import { RefObject, useEffect, useRef, useState } from 'react'
import { ChromelessPlayer } from 'theoplayer'
import { useScript } from 'usehooks-ts'
import { IconButton, Icons } from 'utoniq-ui'

import { Resource } from '@/clients/utoniq-core/schema'
import { TheoPlayerView } from '@/components/organisms/resourcePlayer/TheoPlayerView'
import { config } from '@/config'
import { useAccessToken } from '@/core/auth'
import { captureError } from '@/core/errors'
import { initTheoPlayer } from '@/core/resources/useDRMPlayer'

const libraryLocation = config.theoPlayerLibraryLocation
const functionsEndpoint = config.functionsEndpoint

type Props = {
  resources: {
    label: string
    resource: Resource
  }[]
}

/**
 * @description SelectableVoicePlayer
 * 音声アイコンのボタンを押すとメニューが出て、選択した音声を再生するUI
 * https://zpl.io/agEqNAM
 */

export const SelectableVoicePlayer = ({ resources }: Props) => {
  useScript(libraryLocation + '/THEOplayer.js')
  const { accessToken } = useAccessToken()
  const ref = useRef(null)

  // DRM音源ならtheoPlayer、そうでなければAudioのインスタンスを用意
  const players = resources
    .map(({ resource, label }) => {
      if (resource.drm?.encryptingStatus === 'encrypted') {
        const player = getDRMPlayer({
          accessToken,
          dashUri: resource?.dashUri || '',
          hlsUri: resource?.hlsUri || '',
          ref,
        })

        return player
          ? {
              label,
              player,
            }
          : null
      }

      return {
        label,
        player: new Audio(resource.uri),
      }
    })
    .flatMap(item => (item !== null ? [item] : []))

  useEffect(() => {
    return () => players.forEach(({ player }) => player.pause())
  })

  return (
    <>
      <TheoPlayerView ref={ref} chromeless />
      {ref.current && <PlayerControl players={players} />}
    </>
  )
}

type PlayerControlProps = {
  players: {
    player: HTMLAudioElement | ChromelessPlayer
    label: string
  }[]
}

const PlayerControl = ({ players }: PlayerControlProps) => {
  const [_, observe] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  /**
   * @description
   * 再生終了時にボタンアイコン・チェックマークの表示を変更させる処理
   *
   * 複数個のaudioオブジェクトの再生状態をstateとして管理するのは難しいため、playerのイベントリスナーを登録し、
   * 再生終了毎にPlayerControl内部のstateを更新する事で画面を再描画する。
   */
  useEffect(() => {
    players.forEach(({ player }) => {
      ;(player as ChromelessPlayer).addEventListener('ended', () =>
        observe(state => !state)
      )
    })
  }, [players])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenuClick = (player: HTMLAudioElement | ChromelessPlayer) => {
    handleClose()
    if (player.paused) {
      player.play()
    } else {
      player.pause()
      player.currentTime = 0
    }
  }

  return (
    <>
      {players.some(({ player }) => !player.paused) ? (
        <IconButton.AudioOn onClick={handleClick} />
      ) : (
        <IconButton.AudioOff onClick={handleClick} />
      )}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {players.map(({ player, label }) => (
          <MenuItem
            key={label}
            sx={{
              bgcolor: 'grey.50',
              width: 200,
            }}
            onClick={() => handleMenuClick(player)}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              {!player.paused && <Icons.Check fontSize="xSmall" />}
              <Typography variant="body2">{label}</Typography>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

function getDRMPlayer({
  dashUri,
  hlsUri,
  accessToken,
  ref,
}: {
  dashUri: string
  hlsUri: string
  accessToken: string | null
  ref: RefObject<HTMLElement>
}) {
  const player = initTheoPlayer(ref, { chromeless: true })

  if (player) {
    player.addEventListener('contentprotectionerror', (...err) => {
      captureError(err, 'getDRMPlayer')
    })
    player.source = {
      sources: [
        {
          src: dashUri,
          type: 'application/dash+xml',
          contentProtection: {
            playready: {
              licenseAcquisitionURL: `${functionsEndpoint}/drm/playready`,
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            },
            widevine: {
              licenseAcquisitionURL: `${functionsEndpoint}/drm/widevine`,
              headers: {
                'Content-Type': 'application/octet-stream',
                Accept: 'application/octet-stream',
                Authorization: `Bearer ${accessToken}`,
              },
            },
          },
        },
        {
          src: hlsUri,
          type: 'application/x-mpegURL',
          contentProtection: {
            fairplay: {
              licenseAcquisitionURL: `${functionsEndpoint}/drm/fairplay`,
              certificateURL: config.fairplayCertificateUrl,
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            },
          },
        },
      ],
    }
  }

  return player
}
